import { Component, type ErrorInfo, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App.tsx';
import './styles.css';

interface BoundaryState {
  error: Error | null;
}

/**
 * A white screen tells a photographer nothing and loses their work silently.
 * This at least names what broke and offers the one action that recovers.
 */
class ErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Lumos stopped rendering', error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="scrim">
        <div className="dialog" role="alert">
          <div className="dialog-head">
            <div className="dialog-title">Lumos stopped</div>
          </div>
          <div className="dialog-body">
            <p style={{ margin: 0, lineHeight: 1.55 }}>
              Something in the interface failed and this session cannot continue. Reloading starts
              again from your original files, which were never modified.
            </p>
            <pre
              className="value"
              style={{
                margin: 0,
                padding: '10px 12px',
                border: '1px solid var(--cork-soft)',
                borderRadius: 'var(--radius)',
                background: 'var(--walnut-deep)',
                fontSize: 11,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 200,
                overflowY: 'auto',
              }}
            >
              {error.message || String(error)}
            </pre>
          </div>
          <div className="dialog-foot">
            <button type="button" className="btn ember" onClick={() => window.location.reload()}>
              Reload Lumos
            </button>
          </div>
        </div>
      </div>
    );
  }
}

const container = document.getElementById('root');
if (!container) throw new Error('Lumos could not find its mount element.');

// No StrictMode: its double-invoked mount effects would create, dispose and then
// recreate the WebGL2 context on the same canvas, and a disposed context cannot
// be revived. The renderer's own lifecycle is verified in the manual checklist.
createRoot(container).render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>,
);
